/**
 * Session model shared between the agent banner, layout and routing.
 *
 * The SPA fetches the session from the BFF (`GET /api/session`). The session
 * carries the agent's role, the live Online/Offline interaction state, and the
 * effective permission set used for *client-side* screen gating. The server
 * remains the authoritative policy enforcement point.
 */

import { api } from './api/client.js';
import { setDuplicateSessionBlock } from './duplicate-session-store.js';

export type InteractionState = 'online' | 'offline';

export interface Permission {
  kind: 'screen' | 'api';
  id: string;
  online: boolean;
  offline: boolean;
}

/**
 * Customer/verification context shown in the persistent agent banner.
 *
 * Populated from ID&V outcomes (and later the interaction context). All fields
 * are optional: before a customer is identified the banner shows empty values.
 */
export interface CustomerContext {
  name?: string;
  onlineCardNumber?: string;
  cif?: string;
  assistanceRequired?: string;
  customerVerification?: string;
  /** The live call's CLI/ANI (caller number), from the Genesys screen-pop. Used
   * to compute CLI-match once the customer is identified by card. */
  cli?: string;
  cliMatch?: string;
  /**
   * #201 (ONLINE journey) — the Online Card Number the IVR captured
   * (`Open24Nr` in the Genesys conversation data). When present the ID&V screen
   * auto-runs the card-lookup chain with it, as though the agent had typed it.
   */
  ivrOnlineCardNumber?: string;
  /**
   * #201 (ONLINE journey) — true when the Genesys conversation reports the IVR
   * already verified the customer's PAN (`Verification=PAN`). Only a HINT for the
   * SPA to request the skip; the BFF re-confirms it server-side before honouring
   * it (IDOR-safe).
   */
  ivrPanVerified?: boolean;
  /**
   * #201 — the Genesys conversation id the IVR hints belong to. The ID&V screen
   * auto-triggers ONLY when this matches the live interaction, so a previous
   * call's hints (e.g. left behind by a failed enrichment on a later call)
   * cannot drive a lookup for the wrong customer.
   */
  ivrConversationId?: string;
  /**
   * Issue #72 (@dylan219, 2026-08-21) — an Online card number to auto-run the
   * ID&V card-lookup with, seeded by the Customer Search screen when a
   * NON-privileged role selects a REGISTERED customer while on an ONLINE
   * interaction and clicks "Go to ID&V" on the verification-required warning.
   * The ID&V screen picks it up, drops it into the card field, and runs the
   * SAME card-lookup chain as a manual agent Search (→ the normal PAN option),
   * then clears it (one-shot). Distinct from `ivrOnlineCardNumber`, which is the
   * IVR-captured hint bound to the live conversation (#201) — this one comes from
   * the agent's own Customer Search result, so it is NOT gated on
   * `ivrConversationId` and does NOT carry an IVR PAN-skip. The value is the
   * 10-digit Open24 number (the search returns the 16-digit `OnlineCardNumber`;
   * the caller reduces it via `onlineCardDisplay` so it matches the ID&V card
   * field's 10-digit format).
   */
  pendingIdvCardNumber?: string;
  /**
   * #243 — the live Genesys conversation id the CURRENTLY-IDENTIFIED customer
   * context belongs to. Unlike `ivrConversationId` (set only when the IVR passed
   * an `Open24Nr`), this is stamped for EVERY online interaction that is
   * enriched, and it survives identification (a new customer is spread over the
   * existing context). It lets App.tsx tell a reload of the SAME call (keep the
   * identified customer) from a NEW call (reset the customer so ID&V restarts and
   * the IVR auto-lookup can fire). The customer context persists in sessionStorage
   * to survive a reload within a call — this stops a PRIOR call's customer (and
   * their verification level) leaking into, and blocking the auto-lookup on, a new
   * interaction.
   */
  contextConversationId?: string;
  callsInLastWeek?: number;
  /** Customer date of birth (YYYY-MM-DD) sourced from the gateway; rendered as
   * the second identity question on Medium Security (CCF-4). */
  dob?: string;
  /** Customer segment (EDS / VISA / BUS24) from ID&V. */
  segment?: string;
  /**
   * Online-card / registration type (OPEN24 | BUSINESS24 | VISA | MORTGAGE |
   * BROKER24) from ID&V, used to route deregistration to the correct
   * registration screen (CCF-8/CCF-46).
   */
  registrationType?: string;
  /**
   * The 4-digit Teller ID entered on ID&V, remembered for the duration of the
   * interaction so subsequent Teller-ID-gated calls (Medium Security,
   * Dashboard) reuse it without re-prompting the agent.
   */
  tellerId?: string;
  /**
   * Highest security level the customer has cleared this interaction.
   *   - `'none'`   — card identified, PAN not yet verified;
   *   - `'pan'`    — 3-PAN-digit challenge passed (ID&V complete, CCF-1/CCF-12),
   *                  customer verification flips to "Y"; unlocks Medium Security;
   *   - `'medium'` — Name + DoB + 2FA OTP all passed (CCF-4); unlocks Dashboard.
   *   - `'high'`   — a high/medium security variant (SCRN 4..10) passed all its
   *                  identity questions + 2FA (CCF-53/23/25/26/5/24/42).
   */
  securityLevel?: 'none' | 'pan' | 'medium' | 'high';
  /**
   * #427 — the human-readable name of the security FLOW/variant that granted the
   * current `securityLevel` (e.g. `'EDS'`, `'ECL'`, `'Visa'`, `'Business'`,
   * `'Mortgage'`). Stamped alongside `securityLevel` by the completing security
   * screen so the banner can name which flow was completed — e.g. "EDS Medium
   * Security Verified" instead of a bare "Medium Security Verified". Absent for
   * the base ID&V Medium step (no named variant) — the banner then falls back to
   * the plain "<Level> Security Verified" text.
   */
  verificationVariant?: string;
  /**
   * Set when the customer's online card number was de-registered (3 failed PAN
   * attempts, CCF-8) — telephone banking is blocked; the agent is shown the
   * de-registered scripting + a Register affordance.
   */
  deregistered?: boolean;
  /**
   * How the customer was identified during ID&V (CCF-43). `'online-card'` =
   * identified by Online card number; `'nsc-account'` = identified by Sort Code
   * + account number (the alternative-identification path, CCF-7). Per the
   * CCF-43 update (issue #67) the Customer Authentication Hold Check runs for the
   * ONLINE journey ONLY — the Dashboard reads this to decide whether to run it.
   */
  idvJourney?: 'online-card' | 'nsc-account';
  /** CCF-11: Stop and Think has been triggered and acknowledged by the agent for this call. */
  stopAndThinkAcknowledged?: boolean;
  /**
   * CCF-43 (issue #574): the agent has acknowledged the fraud/AML auth-hold
   * pop-up for this identified customer on this call. Once set, the pop-up is
   * not auto-re-raised when the Dashboard re-mounts. Reset when a new customer
   * is identified (identity fields are cleared on new-customer). The restricted
   * banner + "Review hold details" button remain regardless.
   */
  authHoldAcknowledged?: boolean;
  // Issue #669 — the former `jnHoldAcknowledged` flag was REMOVED
  // (@dylan219, 2026-09-01): the J&N fraud-team-transfer warning no longer has
  // an acknowledge at all — it is a non-dismissible app-shell navigation lock
  // (see `jn-hold-block-store.ts`). There is deliberately no client field to
  // suppress it.
  /** CCF-11: the Stop and Think reasons that triggered the alert (empty if indeterminate path). */
  stopAndThinkReasons?: string[];
  /**
   * Issue #681 (CCF-10 Clear Session): a monotonically-increasing nonce bumped by
   * App's Clear Session handler on EVERY clear-down (both the offline full-reset
   * and the online journey-preserving reset). The ID&V screen watches it and, on
   * each change, resets its LOCAL identification inputs — the Open24 online-card
   * number and the alternative Sort Code + account number fields — back to empty.
   * Clearing the customer context alone does not touch those inputs (they are the
   * ID&V screen's own `useState`, not part of the banner context), so without this
   * signal a Clear Session left the previously-typed credentials sitting in the
   * fields. Absent/undefined ⇒ no clear-down has happened this interaction.
   */
  clearSessionNonce?: number;
}

/**
 * Public Genesys browser-auth config surfaced by the BFF when the runtime
 * Genesys integration is configured. Absent ⇒ the feature is OFF and the SPA
 * keeps its existing demo behaviour.
 */
export interface GenesysBrowserConfig {
  region: string;
  oauthClientId: string;
}

export interface Session {
  upn: string;
  role: string;
  groups: string[];
  state: InteractionState;
  permissions: Permission[];
  /**
   * The agent's 4-digit Teller ID, sourced from their user record/identity by
   * the BFF (issue #4). Present ⇒ the SPA uses it and stops prompting for it;
   * absent ⇒ screens fall back to manual entry.
   */
  tellerId?: string;
  /**
   * #608 CCF-105 — the agent's display name from the `name` OIDC claim,
   * surfaced by the BFF `/api/session` response. Used by the Outbound Security
   * screen to render the agent's full name in the regulatory scripting greeting
   * instead of a placeholder. Absent for Cognito sandbox sessions; the screen
   * falls back to a neutral cue.
   */
  agentName?: string;
  /** Customer banner context; absent until a customer is in context. */
  customer?: CustomerContext;
  /** Public Genesys browser-auth config; absent when Genesys PKCE is off. */
  genesys?: GenesysBrowserConfig;
  /**
   * #343 — whether the BFF already holds a still-valid Genesys token for this
   * session (the token is server-held; the browser never sees it). The SPA uses
   * this to skip the silent-SSO iframe round-trip when already linked.
   */
  genesysLinked?: boolean;
  /**
   * #431 (bucket 3) — when true, the SPA continuously reconciles the Online/
   * Offline journey against live Genesys call state (periodic `/session` poll +
   * re-gate on flip). Config-driven (GENESYS_JOURNEY_RECONCILE); default false ⇒
   * the #357 static-pin behaviour.
   */
  journeyReconcile?: boolean;
  /**
   * CCF-13 (#413) — present ONLY when the Bank gateway signalled (via the
   * `authSessionCount`/`authSessionAgents` response headers) that this ONLINE
   * session's online card number is already active in ANOTHER agent's desktop
   * session. While present the session is FROZEN server-side (every other route
   * 423s); the SPA renders the "DO NOT PROCEED" warning + locks the UI. Never set
   * on offline sessions; cleared by a Clear Session.
   */
  duplicateSessionBlock?: DuplicateSessionBlock;
  /**
   * #716 — the server-validated resolver's DISCRIMINATED reason for THIS
   * session's Online/Offline resolution (mirrors the `genesys.online-state`
   * CloudWatch trace). Purely diagnostic — never used for gating (that reads
   * `state`); surfaced so a "banner Online / treatment Offline" split names its
   * own cause in the browser network/console logs. Absent under AUTH_MODE=mock.
   */
  genesysStateReason?: string;
  /** #716 — client-safe extra discriminators for the `not-a-participant` reason
   * (see {@link GenesysStateDetail}); present only when the resolver emitted them. */
  genesysStateDetail?: GenesysStateDetail;
}

/**
 * #716 — client-safe diagnostic subset accompanying {@link Session.genesysStateReason}
 * on the `not-a-participant` branch. `fetchStatus` 403 = the agent's Genesys
 * role/division lacks conversation read (the supervisor-vs-agent gap → a
 * Genesys-side fix); 401 = token not linked; 404 = wrong org / stale id.
 * `participation: 'no-user-match'` at status 200 = the read succeeded but the
 * agent is not a participant (an id-mismatch, not a permission gap).
 */
export interface GenesysStateDetail {
  fetchStatus?: number;
  participation?: string;
  forgedPinSuspected?: boolean;
}

/**
 * #716 — the resolver reasons that represent a PRESENTED-but-unverified /
 * degraded Online claim: the banner may show Online (launch-URL bootstrap)
 * while every server gate treats the agent Offline. These are worth surfacing
 * to the browser console so a business console-log capture names the cause.
 * The healthy Online (`participant-validated`) and genuine Offline (`no-claim`)
 * reasons are deliberately excluded — they are not the reported symptom.
 */
export const DEGRADED_GENESYS_STATE_REASONS: ReadonlySet<string> = new Set([
  'no-token',
  'pinned-token-not-linked',
  'not-a-participant',
  'genesys-not-configured',
  'genesys-error',
]);

/**
 * #716 — build the one-line browser-console diagnostic for a degraded Genesys
 * Online state, or `undefined` when the state is healthy/genuinely-offline (so
 * the caller logs nothing). Pure + deterministic so it is unit-testable and so
 * the caller can dedupe by the returned string (avoid spamming every ~7s poll).
 */
export function genesysStateConsoleLine(session: Session): string | undefined {
  const reason = session.genesysStateReason;
  if (!reason || !DEGRADED_GENESYS_STATE_REASONS.has(reason)) return undefined;
  const d = session.genesysStateDetail;
  const parts = [`reason=${reason}`, `state=${session.state}`];
  if (d?.fetchStatus !== undefined) parts.push(`fetchStatus=${d.fetchStatus}`);
  if (d?.participation !== undefined)
    parts.push(`participation=${d.participation}`);
  if (d?.forgedPinSuspected) parts.push('forgedPinSuspected=true');
  return (
    `[ptsb-agent] genesys online-state: ${parts.join(' ')} — the banner may show ` +
    `Online while every action is treated Offline; see issue #716`
  );
}

/** CCF-13 (#413) duplicate ONLINE session freeze descriptor (see {@link Session}). */
export interface DuplicateSessionBlock {
  /** The other agent(s) the online card number is in use with. */
  agents: string[];
  /** The gateway's `authSessionCount` (>= 2 when blocked). */
  count: number;
  /** ISO-8601 timestamp of this (subsequent) login attempt. */
  attemptedAt: string;
  /** The pre-composed "WARNING - DO NOT PROCEED" copy. */
  message: string;
}

/** Returns true if the session may view a screen in its current state. */
export function canViewScreen(session: Session, screenId: string): boolean {
  const grant = session.permissions.find(
    (p) => p.kind === 'screen' && p.id === screenId,
  );
  if (!grant) return false; // deny-by-default
  return session.state === 'online' ? grant.online : grant.offline;
}

/**
 * Returns true if the session may perform an API action in its current
 * Online/Offline state. The mirror of {@link canViewScreen} for `api`-kind
 * permissions — deny-by-default, keyed off the ACTUAL per-state write grant.
 *
 * Screens use this for the client-side UX affordance (e.g. rendering a write
 * form read-only when the action is not permitted in the current state, #399),
 * so the agent is told up-front rather than typing and failing at submit. The
 * server-side RBAC gate (`requirePermission`) remains the authoritative
 * enforcement point — this is defence-in-depth's friendly face, never a
 * replacement for it.
 */
export function canPerformApiAction(
  session: Session,
  actionId: string,
): boolean {
  const grant = session.permissions.find(
    (p) => p.kind === 'api' && p.id === actionId,
  );
  if (!grant) return false; // deny-by-default
  return session.state === 'online' ? grant.online : grant.offline;
}

/** Thrown by {@link fetchSession} on a 401 when the app is EMBEDDED (Genesys
 * iframe). The caller renders a sign-in affordance that opens the Cognito hosted
 * UI in a POPUP — an in-iframe redirect to Cognito is blocked by X-Frame-Options.
 * Standalone (top-level) keeps redirecting in place, so it never throws this. */
export class NotAuthenticatedError extends Error {
  constructor() {
    super('not authenticated');
    this.name = 'NotAuthenticatedError';
  }
}

/** True when running inside an iframe (the Genesys-embedded surface), where a
 * top-level redirect to the Cognito hosted UI would be X-Frame-Options-blocked. */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws only on property access, not on
    // the identity comparison; if it ever does, assume embedded (be safe).
    return true;
  }
}

/** Fetches the current session from the BFF (typed against the OpenAPI contract). */
export async function fetchSession(): Promise<Session> {
  const { data, response } = await api.GET('/session');
  if (response.status === 401) {
    // Pattern B: not signed in. EMBEDDED (Genesys iframe): we cannot redirect the
    // iframe to the Cognito hosted UI (X-Frame-Options), so surface a sign-in
    // affordance (popup) — throw so the app renders it.
    if (isEmbedded()) {
      throw new NotAuthenticatedError();
    }
    // STANDALONE (top-level): start the server-side OIDC login; the BFF redirects
    // back once the callback establishes the session. Never-resolving so callers
    // don't flash an error mid-navigation.
    const returnTo = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    window.location.assign(`/auth/login?returnTo=${returnTo}`);
    return new Promise<Session>(() => {});
  }
  if (!response.ok || !data) {
    throw new Error(`session fetch failed: ${response.status}`);
  }
  const session = data as Session;
  // CCF-13 (#413): the session may already carry a duplicate-session freeze
  // (e.g. after a reload while blocked, or when `/session` is polled). Seed the
  // observable store so the freeze overlay renders immediately.
  setDuplicateSessionBlock(session.duplicateSessionBlock ?? null);
  // The wire response is the Session view-model minus the client-side `customer`
  // banner context (added later from ID&V); the generated type is structurally
  // compatible with Session (customer is optional).
  return session;
}
